import { mapHierarchicalValueToCategoryId } from '../../../../util/dateRangeHelpers';

export const FALLBACK_ORDER = 999999;
// Large spacing between marketplace blocks so they don't interleave.
export const BLOCK_SIZE = 10000;

/**
 * Build global ordering indices for all store categories and marketplace groups,
 * based purely on Category Builder configuration (storeCategories + storeCategoryGroups).
 */
export const buildGlobalCategoryOrder = (storeCategories, storeCategoryGroups) => {
  const categories = Array.isArray(storeCategories) ? storeCategories : [];
  const groups = storeCategoryGroups || {};

  const categoryOrderIndex = new Map();
  const marketplaceOrderIndex = new Map();

  if (!categories.length) {
    return { categoryOrderIndex, marketplaceOrderIndex };
  }

  const keyForParent = p => (p == null || p === undefined ? 'null' : p);
  const byParent = new Map();
  categories.forEach(c => {
    const key = keyForParent(c.parentId);
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  });

  byParent.forEach(arr => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));

  const allKeys = [...byParent.keys()];
  const mpKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith('mp:'));
  const savedOrder = Array.isArray(groups.__order) ? groups.__order : [];

  const orderedTopKeys = [
    ...(byParent.has('null') ? ['null'] : []),
    ...savedOrder.filter(k => mpKeys.includes(k)),
    ...mpKeys.filter(k => !savedOrder.includes(k)),
  ];

  let mpBlockIndex = 1;
  orderedTopKeys.forEach(key => {
    if (typeof key === 'string' && key.startsWith('mp:')) {
      marketplaceOrderIndex.set(key, mpBlockIndex++);
    }
  });

  const localIndexPerBlock = new Map();

  const visit = (parentKey, inheritedMpKey = null) => {
    const siblings = byParent.get(parentKey) || [];
    siblings.forEach(c => {
      let mpKey = inheritedMpKey;
      const parentId = c.parentId;
      if (!mpKey && typeof parentId === 'string' && parentId.startsWith('mp:')) {
        mpKey = parentId;
      }

      const blockIdx =
        mpKey && marketplaceOrderIndex.has(mpKey)
          ? marketplaceOrderIndex.get(mpKey)
          : 0;

      const currentLocal = localIndexPerBlock.get(blockIdx) || 0;
      localIndexPerBlock.set(blockIdx, currentLocal + 1);

      const globalIndex = blockIdx * BLOCK_SIZE + currentLocal;
      categoryOrderIndex.set(c.id, globalIndex);

      visit(`sc:${c.id}`, mpKey);
    });
  };

  orderedTopKeys.forEach(topKey => {
    const inheritedMpKey =
      typeof topKey === 'string' && topKey.startsWith('mp:') ? topKey : null;
    visit(topKey, inheritedMpKey);
  });

  return { categoryOrderIndex, marketplaceOrderIndex };
};

export const sortHierarchicalMenuItemsByOrder = (items, user, locale) => {
  if (!items?.length) return items ?? [];

  const storeCategories = user?.attributes?.profile?.publicData?.storeCategories || [];
  const storeCategoryGroups = user?.attributes?.profile?.publicData?.storeCategoryGroups || {};

  const { categoryOrderIndex, marketplaceOrderIndex } = buildGlobalCategoryOrder(
    storeCategories,
    storeCategoryGroups
  );

  const getSortKey = item => {
    const meta = mapHierarchicalValueToCategoryId(item.value, user, locale);
    if (!meta) return FALLBACK_ORDER;

    if (meta.depth === 0) {
      const mpIdx = marketplaceOrderIndex.get(meta.categoryId);
      return mpIdx != null ? (mpIdx + 1) * BLOCK_SIZE : FALLBACK_ORDER * 2;
    }

    const catIdx = categoryOrderIndex.get(meta.categoryId);
    return catIdx != null ? catIdx : FALLBACK_ORDER;
  };

  const sorted = [...items].sort((a, b) => getSortKey(a) - getSortKey(b));
  return sorted.map(item => ({
    ...item,
    data: item.data?.length ? sortHierarchicalMenuItemsByOrder(item.data, user, locale) : item.data,
  }));
};

export const filterHierarchicalMenuByVisibility = (
  items,
  user,
  locale,
  visibleCategoryIds,
  visibleMarketplaceKeys
) => {
  if (!items?.length) return items ?? [];
  return items
    .map(item => {
      const meta = mapHierarchicalValueToCategoryId(item.value, user, locale);
      if (!meta) return null;
      const visible =
        meta.depth === 0
          ? visibleMarketplaceKeys.has(meta.categoryId)
          : visibleCategoryIds.has(meta.categoryId);
      if (!visible) return null;
      const filteredData = item.data?.length
        ? filterHierarchicalMenuByVisibility(
            item.data,
            user,
            locale,
            visibleCategoryIds,
            visibleMarketplaceKeys
          )
        : item.data;
      return { ...item, data: filteredData };
    })
    .filter(Boolean);
};

export const getRootMpKeyFromItem = (item, user, locale) => {
  const meta = mapHierarchicalValueToCategoryId(item?.value, user, locale);
  return meta && meta.depth === 0 && meta.categoryId?.startsWith('mp:') ? meta.categoryId : null;
};

export const isRootMpItem = (item, user, locale) => !!getRootMpKeyFromItem(item, user, locale);

export const isMarketplaceRootVisible = (mpKey, storeCategoryGroups) => {
  if (!mpKey || !mpKey.startsWith('mp:')) return false;
  return storeCategoryGroups?.[mpKey]?.visible !== false;
};

export const composeCategoryList = (
  filteredItems,
  rawItems,
  user,
  locale,
  storeCategoryGroups,
  showAllCategories,
  showAllCustomCategories
) => {
  const items = rawItems ?? [];
  if (!showAllCategories) {
    return showAllCustomCategories ? filteredItems : [];
  }
  const customRoots = showAllCustomCategories ? filteredItems : [];
  const mpKeysInCustom = new Set(
    customRoots.map(item => getRootMpKeyFromItem(item, user, locale)).filter(Boolean)
  );
  const marketplaceOnlyRoots = items.filter(item => {
    const mpKey = getRootMpKeyFromItem(item, user, locale);
    return (
      !!mpKey &&
      (item.count || 0) > 0 &&
      isMarketplaceRootVisible(mpKey, storeCategoryGroups) &&
      !mpKeysInCustom.has(mpKey)
    );
  });
  return [...customRoots, ...marketplaceOnlyRoots];
};

/** Algolia hierarchical attributes — must stay in sync with indexing & refinement mapping. */
export const STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES = [
  'publicData.storeCategory.lvl0',
  'publicData.storeCategory.lvl1',
  'publicData.storeCategory.lvl2',
  'publicData.storeCategory.lvl3',
  'publicData.storeCategory.lvl4',
];

export const HIERARCHICAL_SEPARATOR = ' > ';

/**
 * Build the same nested item shape react-instantsearch's `useHierarchicalMenu`
 * produces ({ value, label, count, isRefined, data }), but from a raw Algolia
 * facets response instead of the live (query-scoped) menu.
 *
 * The live menu only ever contains categories present in the *current* result
 * set, so a keyword search collapses it to the one matching branch. Passing the
 * facets from a keyword-independent `search('', { facets })` call here gives the
 * full store catalogue, which stays stable while the shopper types (RAS-2591).
 *
 * `value` is the full path (e.g. "Sports & Recreation > Camping"), matching what
 * `refine()` and `mapHierarchicalValueToCategoryId` expect; `label` is the last
 * segment. `count` is the store-wide count (later overridden with the live,
 * query-scoped count by decorateTreeWithLiveFacets).
 *
 * @param {Object} facets - `facets` map from an Algolia search response
 * @param {string} separator - hierarchical separator (defaults to " > ")
 * @returns {Array} nested menu items
 */
export const buildHierarchicalTreeFromFacets = (
  facets,
  separator = HIERARCHICAL_SEPARATOR
) => {
  if (!facets) return [];

  const roots = [];
  const nodeByValue = new Map();

  STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES.forEach((attr, depth) => {
    const facetValues = facets[attr];
    if (!facetValues) return;

    Object.keys(facetValues).forEach(value => {
      if (nodeByValue.has(value)) return;

      const segments = value.split(separator);
      const node = {
        value,
        label: (segments[segments.length - 1] || value).trim(),
        count: facetValues[value],
        isRefined: false,
        data: [],
      };
      nodeByValue.set(value, node);

      if (depth === 0) {
        roots.push(node);
        return;
      }

      const parentValue = segments.slice(0, -1).join(separator);
      const parent = nodeByValue.get(parentValue);
      // Orphan safety: if a parent level is somehow missing from the facets,
      // surface the node at the root rather than dropping it.
      (parent ? parent.data : roots).push(node);
    });
  });

  // Match useHierarchicalMenu's convention of `data: null` for leaf nodes.
  const normalize = nodes =>
    nodes.map(node => ({
      ...node,
      data: node.data.length ? normalize(node.data) : null,
    }));

  return normalize(roots);
};

/**
 * Flatten a (live or full) hierarchical menu into a Map keyed by the node's
 * full-path `value`, so a node in one tree can be looked up against the other.
 *
 * @param {Array} items - nested hierarchical menu items
 * @param {Map} [map] - accumulator (used internally for recursion)
 * @returns {Map<string, { count: number, isRefined: boolean }>}
 */
export const flattenLiveFacetMap = (items, map = new Map()) => {
  (items || []).forEach(item => {
    map.set(item.value, { count: item.count, isRefined: !!item.isRefined });
    if (item.data && item.data.length) {
      flattenLiveFacetMap(item.data, map);
    }
  });
  return map;
};

/**
 * Overlay the live, query-scoped facet data (counts + refined state) onto the
 * full catalogue tree from buildHierarchicalTreeFromFacets.
 *
 * - A node present in the live menu keeps the live count and refined flag and is
 *   marked `matched` (it has results for the current query).
 * - A node absent from the live menu keeps its store-wide count when no keyword
 *   is active, but shows no count during a keyword search (RAS-2591 — a bare
 *   "(12)" next to a highlighted "(7)" reads as if both match the search).
 * - `matchedDescendant` bubbles up so a collapsed parent can still be highlighted
 *   when one of its children matches.
 *
 * @param {Array} tree - full catalogue tree
 * @param {Array} liveItems - live (query-scoped) useHierarchicalMenu items
 * @param {boolean} keywordActive - whether a keyword search is currently applied
 * @returns {Array} decorated tree (same shape, plus `matched` / `matchedDescendant`)
 */
export const decorateTreeWithLiveFacets = (tree, liveItems, keywordActive) => {
  const liveMap = flattenLiveFacetMap(liveItems);

  const walk = nodes =>
    (nodes || []).map(node => {
      const live = liveMap.get(node.value);
      const data = node.data && node.data.length ? walk(node.data) : node.data;
      const matched = !!live;
      const matchedDescendant = !!(data || []).some(
        child => child.matched || child.matchedDescendant
      );

      return {
        ...node,
        data,
        count: live
          ? live.count
          : keywordActive
            ? null
            : node.count,
        isRefined: live ? live.isRefined : false,
        matched,
        matchedDescendant,
      };
    });

  return walk(tree);
};
